import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from "@angular/forms";
import {FormHelperService} from "../../../../../shared/services/form-helper.service";
import {ReplaySubject} from "rxjs";
import {Contract} from "../../../../../shared/interface/contract";
import {first, takeUntil} from "rxjs/operators";
import {InvoiceDocumentService} from "../../../../../shared/services/invoice-document.service";
import {Uuid} from "../../../../../shared/interface/response/uuid";
import {HttpErrorResponse} from "@angular/common/http";
import {MatDialog} from "@angular/material/dialog";
import {validNip} from "../../../../../shared/validator/nip-validator";
import {validPesel} from "../../../../../shared/validator/pesel-validator";

@Component({
  selector: 'app-invoice-document-create',
  templateUrl: './invoice-document-create.component.html',
  styleUrls: ['./invoice-document-create.component.scss']
})
export class InvoiceDocumentCreateComponent implements OnInit, OnDestroy {
  disabledInvoiceNote = false;
  processingRequest = false;

  invoiceNoteForm: FormGroup;
  companyControl: FormControl;
  contractControl: FormControl;
  grossControl: FormControl;
  reasonControl: FormControl;
  withoutContractControl: FormControl;
  recipientNameControl: FormControl;
  recipientStreetControl: FormControl;
  recipientZipCodeControl: FormControl;
  recipientPostOfficeControl: FormControl;

  recipientNipControl: FormControl;
  recipientPeselControl: FormControl;

  useNipControl = new FormControl(true);
  usePeselControl = new FormControl(false);

  companyId: number | null = null;
  selectedContract?: Contract;

  errors: any = {};


  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private snackBar: MatSnackBar,
    private invoiceDocumentService: InvoiceDocumentService,
    private router: Router,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private formHelper: FormHelperService,
  ) {

    this.invoiceNoteForm = this.fb.group({
      company: this.companyControl = fb.control('', [Validators.required]),
      contract: this.contractControl = fb.control({ value: '', disabled: true }, [Validators.required]),
      gross: this.grossControl = fb.control('', [Validators.required, Validators.min(0.01)]),
      reason: this.reasonControl = fb.control('', [Validators.required]),
      withoutContract: this.withoutContractControl = fb.control(false),
      recipientName: this.recipientNameControl = fb.control({value: '', disabled: true}),
      recipientStreet: this.recipientStreetControl = fb.control({value: '', disabled: true}),
      recipientZipCode: this.recipientZipCodeControl = fb.control({value: '', disabled: true}),
      recipientPostOffice: this.recipientPostOfficeControl = fb.control({value: '', disabled: true}),
      recipientNip: this.recipientNipControl = fb.control({value: '', disabled: true}),
      recipientPesel: this.recipientPeselControl = fb.control({value: '', disabled: true}),

    });
    this.usePeselControl = fb.control(false);
  }

  ngOnInit(): void {
    this.companyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((company: any) => {
        this.companyId = company?.id ?? company?.uuid ?? company ?? null;

        this.contractControl.setValue('');
        this.selectedContract = undefined;

        if (this.companyId && !this.withoutContractControl.value) {
          this.contractControl.enable();
        } else {
          this.contractControl.disable();
        }
      });

    this.withoutContractControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((withoutContract: boolean) => this.updateWithoutContract(withoutContract));

    this.usePeselControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.withoutContractControl.value) {
          this.updateIdentifierControls();
        }
      });
  }

  private updateWithoutContract(withoutContract: boolean): void {
    this.selectedContract = undefined;
    this.contractControl.reset({value: null, disabled: true}, {emitEvent: false});

    if (withoutContract) {
      this.contractControl.clearValidators();
    } else {
      this.contractControl.setValidators([Validators.required]);
      if (this.companyId) {
        this.contractControl.enable({emitEvent: false});
      }
    }
    this.contractControl.updateValueAndValidity({emitEvent: false});

    const recipientControls: Array<[FormControl, any[]]> = [
      [this.recipientNameControl, [Validators.required, Validators.pattern(/\S/)]],
      [this.recipientStreetControl, [Validators.required, Validators.pattern(/\S/)]],
      [this.recipientZipCodeControl, [Validators.required, Validators.pattern(/^\d{2}-\d{3}$/)]],
      [this.recipientPostOfficeControl, [Validators.required, Validators.pattern(/\S/)]],
    ];

    recipientControls.forEach(([control, validators]) => {
      if (withoutContract) {
        control.enable({emitEvent: false});
        control.setValidators(validators);
      } else {
        control.reset({value: '', disabled: true}, {emitEvent: false});
        control.clearValidators();
      }
      control.updateValueAndValidity({emitEvent: false});
    });

    // Każde wejście w tryb „Bez umowy” rozpoczynamy od NIP.
    this.usePeselControl.setValue(false, {emitEvent: false});
    this.updateIdentifierControls();
  }


  private updateIdentifierControls(): void {
    const withoutContract = this.withoutContractControl.value === true;
    const useNip = withoutContract && this.useNipControl.value === true;
    const usePesel = withoutContract && this.usePeselControl.value === true;

    const identifierControls: Array<[FormControl, boolean, ValidatorFn[]]> = [
      [this.recipientNipControl, useNip,
        [
          Validators.required,
          validNip()
        ]
      ],
      [
        this.recipientPeselControl,
        usePesel,
        [
          Validators.required,
          validPesel()
        ]
      ]
    ];

    identifierControls.forEach(([control, active, validators]) => {
      if (active) {
        control.enable({emitEvent: false});
        control.setValidators(validators);
      } else {
        control.reset({value: '', disabled: true}, {emitEvent: false});
        control.clearValidators();
      }

      control.updateValueAndValidity({emitEvent: false});
    });
  }


  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  contractChanged(contract: Contract): void {
    this.selectedContract = contract;
  }

  save(): void {
    if (this.invoiceNoteForm.invalid || this.processingRequest) {
      this.invoiceNoteForm.markAllAsTouched();
      this.snackBar.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }
    this.disabledInvoiceNote = true;
    this.processingRequest = true;
    const payload = {
      ...this.invoiceNoteForm.value,
      company: this.companyId,
      contract: this.withoutContractControl.value ? null : this.contractControl.value,
      nip: this.withoutContractControl.value && this.usePeselControl.value === false
        ? String(this.recipientNipControl.value ?? '').trim()
        : null,
      pesel: this.withoutContractControl.value && this.usePeselControl.value === true
        ? String(this.recipientPeselControl.value ?? '').trim()
        : null,
    };
    this.invoiceDocumentService.sendForm(payload)
      .pipe(
        first()
      ).subscribe((response: Uuid) => {
      this.snackBar.open('Pomyślnie utworzono notę', '', {
        duration: 2000,
      });
      this.processingRequest = false;
      this.router.navigate(['/noty/szczegoly', response.uuid]);
    }, (error: HttpErrorResponse) => {
      this.disabledInvoiceNote = false;
      this.processingRequest = false;

      this.snackBar.open('Podczas przesyłania formularza wystąpił błąd', error.status.toString(), {
        duration: 2000,
      });

      if (error.status === 400) {
        this.errors = error.error?.error?.list ?? {};
        this.formHelper.setBackendErrorState(this.invoiceNoteForm, this.errors);
      }
    });


  }

  cancel(): void {
    this.router.navigate(['/noty']);
  }

  selectIdentifier(type: 'nip' | 'pesel'): void {
    const usePesel = type === 'pesel';

    this.useNipControl.setValue(!usePesel, {emitEvent: false});
    this.usePeselControl.setValue(usePesel, {emitEvent: false});

    if (this.withoutContractControl.value) {
      this.updateIdentifierControls();
    }
  }
}
